import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const CodeBlock = ({ language, title, children }) => (
  <div className="my-4">
    {title && <div className="text-xs text-slate-500 font-medium mb-1">{title}</div>}
    <pre className={`bg-slate-900 rounded-lg p-4 overflow-x-auto text-sm border border-slate-700`}>
      <code className={`language-${language} text-slate-200`}>{children.trim()}</code>
    </pre>
  </div>
);

const EndpointCard = ({ method, path, description, params, responseExample }) => {
  const [expanded, setExpanded] = useState(false);
  const methodColors = {
    GET: 'bg-green-500/20 text-green-400 border-green-500/30',
    POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  return (
    <div className="border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-700/30 transition text-left"
      >
        <span className={`px-2.5 py-1 text-xs font-bold rounded border ${methodColors[method]}`}>
          {method}
        </span>
        <code className="text-emerald-500 font-mono text-sm flex-1">{path}</code>
        <span className="text-slate-500 text-sm hidden md:block">{description}</span>
        <svg className={`w-4 h-4 text-slate-400 transition ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-700 pt-4">
          <p className="text-slate-300 text-sm mb-4">{description}</p>
          {params && (
            <>
              <h4 className="text-white font-semibold text-sm mb-2">Parameters</h4>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="py-2 px-3 text-left text-slate-400 font-medium">Name</th>
                      <th className="py-2 px-3 text-left text-slate-400 font-medium">Type</th>
                      <th className="py-2 px-3 text-left text-slate-400 font-medium">Required</th>
                      <th className="py-2 px-3 text-left text-slate-400 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {params.map((p, i) => (
                      <tr key={i}>
                        <td className="py-2 px-3"><code className="text-emerald-500 text-xs">{p.name}</code></td>
                        <td className="py-2 px-3 text-slate-400">{p.type}</td>
                        <td className="py-2 px-3 text-slate-400">{p.required ? 'Yes' : 'No'}</td>
                        <td className="py-2 px-3 text-slate-400">{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {responseExample && (
            <>
              <h4 className="text-white font-semibold text-sm mb-2">Response Example</h4>
              <CodeBlock language="json">{responseExample}</CodeBlock>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function ApiPage() {
  return (
    <div className="bg-slate-800 text-white py-16 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>API Documentation — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/api-docs" />
        <meta name="description" content="REST API docs for sex offender registry data. Endpoints for search, geolocation, and batch queries with code examples." />
        <meta property="og:title" content="API Documentation — CommunityGuardAPI" />
        <meta property="og:description" content="REST API docs for sex offender registry data. Endpoints for search, geolocation, and batch queries with code examples." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/api-docs" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            API <span className="text-emerald-500">Documentation</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Access nationwide sex offender registry data via our REST API. Search by name, location, or coordinates with comprehensive filtering.
          </p>
        </div>

        {/* Quick Start */}
        <div className="bg-gradient-to-r from-slate-900/30 to-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 mb-12">
          <h2 className="text-xl font-bold text-white mb-3">Quick Start</h2>
          <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm mb-4">
            <li><Link to="/signup" className="text-emerald-500 hover:text-amber-400">Create a free account</Link> (100 API calls/month included)</li>
            <li>Generate an API key from your <Link to="/dashboard" className="text-emerald-500 hover:text-amber-400">Dashboard</Link></li>
            <li>Include the key in your requests via the <code className="bg-slate-700 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header</li>
          </ol>
          <CodeBlock language="bash" title="Example request">{`curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/search/?lastName=Smith&state=FL&page_size=10"`}</CodeBlock>
        </div>

        {/* Authentication */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-4">Authentication</h2>
          <p className="text-slate-300 mb-4">
            All API requests require an API key. Include your key in the <code className="bg-slate-700 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> request header.
          </p>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-sm space-y-2">
            <div className="flex gap-3 items-center text-slate-300">
              <span className="text-slate-500 w-20">Header:</span>
              <code className="text-emerald-500">X-API-Key: pr_live_xxxxxxxxxxxx</code>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-3">
            API keys use the <code className="bg-slate-700 px-1 py-0.5 rounded text-xs">pr_live_</code> prefix and are 60 characters long.
            Keys are hashed with SHA-256 and stored securely. The full key is only shown once at creation.
          </p>
        </section>

        {/* Base URL */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-4">Base URL</h2>
          <CodeBlock language="text">{`https://communityguardapi.com/api/data/`}</CodeBlock>
        </section>

        {/* Rate Limits */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-4">Rate Limits & Usage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-emerald-500 font-semibold">Free Tier</div>
              <div className="text-2xl font-bold text-white mt-1">100</div>
              <div className="text-slate-400 text-sm">calls/month</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-slate-300 font-semibold">With Payment</div>
              <div className="text-2xl font-bold text-white mt-1">Pay-as-you-go</div>
              <div className="text-slate-400 text-sm">$0.08–$0.12 per call</div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-slate-300 font-semibold">Rate Limit</div>
              <div className="text-2xl font-bold text-white mt-1">60</div>
              <div className="text-slate-400 text-sm">requests/minute</div>
            </div>
          </div>
          <p className="text-slate-400 text-sm">
            Usage headers are included in every response: <code className="bg-slate-700 px-1 py-0.5 rounded text-xs">X-API-Usage</code>, <code className="bg-slate-700 px-1 py-0.5 rounded text-xs">X-API-Limit</code>, <code className="bg-slate-700 px-1 py-0.5 rounded text-xs">X-API-Remaining</code>.
          </p>
        </section>

        {/* Endpoints */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-6">Endpoints</h2>
          <div className="space-y-3">
            <EndpointCard
              method="GET"
              path="/api/data/search/"
              description="Search offenders by name, location, and filters with pagination"
              params={[
                { name: 'q', type: 'string', required: false, desc: 'General search (searches name, address, registration ID, aliases)' },
                { name: 'firstName', type: 'string', required: false, desc: 'Filter by first name (case-insensitive partial match)' },
                { name: 'lastName', type: 'string', required: false, desc: 'Filter by last name (case-insensitive partial match)' },
                { name: 'zipCode', type: 'string', required: false, desc: 'Filter by ZIP code' },
                { name: 'city', type: 'string', required: false, desc: 'Filter by city' },
                { name: 'state', type: 'string', required: false, desc: 'Filter by state (2-letter abbreviation, e.g., FL, NY)' },
                { name: 'risk_level', type: 'string', required: false, desc: 'Filter by risk level (e.g., High, Moderate, Low)' },
                { name: 'has_child_victim', type: 'boolean', required: false, desc: 'Filter by child victim involvement (true/false)' },
                { name: 'sex_offense_type', type: 'string', required: false, desc: 'Filter by offense type (e.g., Rape, Sexual Battery)' },
                { name: 'designation', type: 'string', required: false, desc: 'Filter by designation (e.g., Sexual Predator)' },
                { name: 'sex', type: 'string', required: false, desc: 'Filter by sex (M or F)' },
                { name: 'age_min', type: 'integer', required: false, desc: 'Minimum age' },
                { name: 'age_max', type: 'integer', required: false, desc: 'Maximum age' },
                { name: 'sort', type: 'string', required: false, desc: 'Sort order: name_asc, name_desc, age_asc, age_desc, newest_conviction, distance' },
                { name: 'latitude', type: 'float', required: false, desc: 'User latitude (required for sort=distance)' },
                { name: 'longitude', type: 'float', required: false, desc: 'User longitude (required for sort=distance)' },
                { name: 'page', type: 'integer', required: false, desc: 'Page number (1-based, default 1)' },
                { name: 'page_size', type: 'integer', required: false, desc: 'Results per page (default 50, max 200)' },
              ]}
              responseExample={`{
  "results": [
    {
      "id": 12345,
      "full_name": "John A Smith",
      "registration_id": "FL-2024-12345",
      "first_name": "John",
      "last_name": "Smith",
      "middle_name": "A",
      "age": 45,
      "date_of_birth": "1981-03-15",
      "sex": "Male",
      "race": "White",
      "height": "5'11\\"",
      "weight": "185",
      "hair_color": "Brown",
      "eye_color": "Blue",
      "risk_level": "High",
      "designation": "Sexual Predator",
      "address_line1": "123 Main St",
      "city": "Miami",
      "state_id": "FL",
      "zip_code": "33101",
      "latitude": 25.7617,
      "longitude": -80.1918,
      "offender_image_url": "https://...",
      "tracking_url": "https://...",
      "aliases": [
        { "first_name": "Johnny", "last_name": "Smith" }
      ],
      "detailed_addresses": [
        {
          "address_type": "Residential",
          "street_line1": "123 Main St",
          "city": "Miami",
          "state": "FL",
          "zip_code": "33101"
        }
      ],
      "distance_miles": null
    }
  ],
  "pagination": {
    "page": 1,
    "page_size": 50,
    "total_count": 847,
    "total_pages": 17,
    "has_next": true,
    "has_previous": false
  }
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/data/offenders/{id}/"
              description="Get detailed information for a single offender by database ID"
              params={[
                { name: 'id', type: 'integer', required: true, desc: 'Offender database ID (from search results)' },
              ]}
              responseExample={`{
  "id": 12345,
  "full_name": "John A Smith",
  "registration_id": "FL-2024-12345",
  "first_name": "John",
  "last_name": "Smith",
  "age": 45,
  "risk_level": "High",
  "designation": "Sexual Predator",
  "address_line1": "123 Main St",
  "city": "Miami",
  "state_id": "FL",
  "zip_code": "33101",
  "latitude": 25.7617,
  "longitude": -80.1918,
  "aliases": [...],
  "distinctive_features": [...],
  "detailed_addresses": [...],
  "offender_image_url": "https://...",
  "tracking_url": "https://..."
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/data/offenders/nearby/"
              description="Find offenders within a radius of a geographic point"
              params={[
                { name: 'latitude', type: 'float', required: true, desc: 'Center point latitude' },
                { name: 'longitude', type: 'float', required: true, desc: 'Center point longitude' },
                { name: 'radius', type: 'float', required: false, desc: 'Search radius in miles (default 5)' },
              ]}
              responseExample={`[
  {
    "id": 12345,
    "full_name": "John Smith",
    "distance_miles": 0.3,
    "latitude": 25.7617,
    "longitude": -80.1918,
    ...
  },
  {
    "id": 12346,
    "full_name": "Jane Doe",
    "distance_miles": 1.2,
    ...
  }
]`}
            />

            <EndpointCard
              method="GET"
              path="/api/data/stats/"
              description="Aggregate statistics about offenders in the database"
              params={[
                { name: 'state', type: 'string', required: false, desc: 'Scope stats to a specific state (2-letter code)' },
              ]}
              responseExample={`{
  "total": 730000,
  "by_state": [
    { "state_id": "FL", "count": 170000 },
    { "state_id": "TX", "count": 133000 },
    ...
  ],
  "by_risk_level": [
    { "risk_level": "High", "count": 45000 },
    ...
  ],
  "by_designation": [...],
  "by_sex": [...],
  "by_offense_type": [...],
  "has_child_victim": {
    "true": 89000,
    "false": 410000,
    "unknown": 231000
  }
}`}
            />
          </div>
        </section>

        {/* Official SDKs */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-4">Official SDKs</h2>
          <p className="text-slate-400 text-sm mb-4">Install an official client library to get started in seconds.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🐍</span>
                <div>
                  <h3 className="text-white font-semibold">Python</h3>
                  <code className="text-xs text-slate-400">pip install communityguard</code>
                </div>
              </div>
              <CodeBlock language="python">{`from communityguard import CommunityGuard

api = CommunityGuard("pr_live_your_key_here")
results = api.search(lastName="Smith", state="FL")
nearby = api.nearby(38.90, -77.04, radius=5)`}</CodeBlock>
              <a href="https://github.com/stefanverleysen/communityguard-python" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-amber-400 text-sm font-medium transition">View on GitHub &rarr;</a>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">⬡</span>
                <div>
                  <h3 className="text-white font-semibold">JavaScript / Node.js</h3>
                  <code className="text-xs text-slate-400">npm install communityguard</code>
                </div>
              </div>
              <CodeBlock language="javascript">{`import CommunityGuard from 'communityguard';

const api = new CommunityGuard('pr_live_your_key_here');
const results = await api.search({ lastName: 'Smith', state: 'FL' });
const nearby = await api.nearby(38.90, -77.04, 5);`}</CodeBlock>
              <a href="https://github.com/stefanverleysen/communityguard-js" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-amber-400 text-sm font-medium transition">View on GitHub &rarr;</a>
            </div>
          </div>
        </section>

        {/* Integration Examples */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-6">Raw HTTP Examples</h2>

          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-3">JavaScript / Node.js</h3>
              <CodeBlock language="javascript">{`const response = await fetch(
  'https://communityguardapi.com/api/data/search/?lastName=Smith&state=FL&page_size=10',
  {
    headers: {
      'X-API-Key': 'pr_live_your_key_here',
      'Content-Type': 'application/json',
    },
  }
);
const data = await response.json();
console.log(\`Found \${data.pagination.total_count} offenders\`);
data.results.forEach(offender => {
  console.log(\`\${offender.full_name} - \${offender.city}, \${offender.state_id}\`);
});`}</CodeBlock>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-3">Python</h3>
              <CodeBlock language="python">{`import requests

API_KEY = "pr_live_your_key_here"
BASE_URL = "https://communityguardapi.com/api/data"

# Search by name and state
response = requests.get(
    f"{BASE_URL}/search/",
    headers={"X-API-Key": API_KEY},
    params={"lastName": "Smith", "state": "FL", "page_size": 10},
)
data = response.json()
print(f"Found {data['pagination']['total_count']} offenders")

# Find nearby offenders
nearby = requests.get(
    f"{BASE_URL}/offenders/nearby/",
    headers={"X-API-Key": API_KEY},
    params={"latitude": 25.7617, "longitude": -80.1918, "radius": 2},
)
for offender in nearby.json():
    print(f"{offender['full_name']} - {offender['distance_miles']} mi away")`}</CodeBlock>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-3">cURL</h3>
              <CodeBlock language="bash">{`# Search by name
curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/search/?firstName=John&lastName=Doe&state=NY"

# Get offender by ID
curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/offenders/12345/"

# Find nearby offenders (within 3 miles)
curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/offenders/nearby/?latitude=40.7128&longitude=-74.0060&radius=3"

# Get statistics
curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/stats/?state=FL"`}</CodeBlock>
            </div>
          </div>
        </section>

        {/* Error Codes */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-6">Error Codes</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Status</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Code</th>
                  <th className="py-3 px-4 text-left text-slate-400 font-medium">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                <tr>
                  <td className="py-3 px-4 text-yellow-400 font-mono">400</td>
                  <td className="py-3 px-4 text-slate-300">bad_request</td>
                  <td className="py-3 px-4 text-slate-400">Missing or invalid parameters</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-red-400 font-mono">401</td>
                  <td className="py-3 px-4 text-slate-300">invalid_api_key</td>
                  <td className="py-3 px-4 text-slate-400">API key is invalid or revoked</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-red-400 font-mono">402</td>
                  <td className="py-3 px-4 text-slate-300">usage_limit_exceeded</td>
                  <td className="py-3 px-4 text-slate-400">Free tier exceeded, payment method required</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-red-400 font-mono">404</td>
                  <td className="py-3 px-4 text-slate-300">not_found</td>
                  <td className="py-3 px-4 text-slate-400">Offender not found</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-red-400 font-mono">500</td>
                  <td className="py-3 px-4 text-slate-300">server_error</td>
                  <td className="py-3 px-4 text-slate-400">Internal server error</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Support */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-emerald-500 mb-4">Support</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-2">API Status</h3>
              <p className="text-slate-400 text-sm">Check real-time API status and uptime.</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                <span className="text-green-400 text-sm font-medium">All Systems Operational</span>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-2">Email Support</h3>
              <p className="text-slate-400 text-sm">Get help with integration or billing.</p>
              <a href="mailto:support@communityguardapi.com" className="mt-3 text-emerald-500 hover:text-amber-400 text-sm font-medium block transition">
                support@communityguardapi.com
              </a>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
              <h3 className="text-white font-semibold mb-2">Getting Started</h3>
              <p className="text-slate-400 text-sm">Create an account and generate your API key in under a minute.</p>
              <Link to="/signup" className="mt-3 text-emerald-500 hover:text-amber-400 text-sm font-medium block transition">
                Create Free Account &rarr;
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-slate-900/30 to-emerald-500/10 border border-emerald-500/30 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-3">Start Building Today</h2>
          <p className="text-slate-300 mb-6">100 free API calls per month. No credit card required.</p>
          <Link
            to="/signup"
            className="inline-block px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow-lg transition"
          >
            Get Your API Key
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ApiPage;
